import intersection from 'lodash/intersection';
import { useCallback, useMemo } from 'react';
import type { UseFormGetValues, UseFormSetValue } from 'react-hook-form';
import type { Event } from '@bridebook/web/lib/events/types';
import type { RsvpQuestionFormValues } from '@bridebook/web/lib/rsvp/types';

interface IEventOption {
  value: string;
  label: string;
}

interface IEventOptionsArgs {
  isLoading: boolean;
  unifiedEvents: Event[];
}

interface IArgs extends IEventOptionsArgs {
  getValues: UseFormGetValues<RsvpQuestionFormValues>;
  setValue: UseFormSetValue<RsvpQuestionFormValues>;
}

const useEventOptions = ({ isLoading, unifiedEvents }: IEventOptionsArgs): IEventOption[] =>
  useMemo(
    () =>
      (isLoading ? [] : unifiedEvents).map((unifiedEvent) => ({
        value: unifiedEvent.id,
        label: unifiedEvent.name,
      })),
    [isLoading, unifiedEvents],
  );

export const useEventOptionsWithHandlers = ({
  isLoading,
  unifiedEvents,
  getValues,
  setValue,
}: IArgs) => {
  const eventOptions = useEventOptions({ isLoading, unifiedEvents });
  const handleSelectCheckbox = useCallback(
    (value: string) => {
      const selectedEvents = getValues('events');
      if (selectedEvents.includes(value)) {
        setValue(
          'events',
          selectedEvents.filter((ev) => ev !== value),
        );
      } else {
        const selectedEventsInOrder = intersection(
          eventOptions.map((e) => e.value),
          [...selectedEvents, value],
        );
        setValue('events', selectedEventsInOrder);
      }
    },
    [eventOptions, getValues, setValue],
  );

  const handleSelectAllCheckbox = useCallback(() => {
    const selected = getValues('events');
    if (selected.length === eventOptions.length) {
      setValue('events', []);
    } else {
      setValue(
        'events',
        eventOptions.map((e) => e.value),
      );
    }
  }, [eventOptions, getValues, setValue]);

  return { eventOptions, handleSelectCheckbox, handleSelectAllCheckbox };
};
